import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import * as _ from "lodash";
import { IItemRecord } from "records/item/item";
import type { IScope } from "reducers/scope";
import Image360Icon from "components/icons/image-360";
import VideoPlayIcon from "components/icons/video-play";
import IconButton from "@material-ui/core/IconButton";
import makeStyles from "@material-ui/core/styles/makeStyles";
import VideoPoster from "components/product-media/video-poster";
import { MEDIA_TYPE_360, MEDIA_TYPE_IMAGE, MEDIA_TYPE_THREEDIUM, MEDIA_TYPE_VIDEO } from "constants/media";
import classnames from "classnames";
import { theme } from "styles/theme";
import Slider, { Settings } from "react-slick";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import ImageSafe from "components/image";
import { useGetSafeImages } from "helpers/product-media-helpers";
import { IMediaImage } from "constants/product-media";
import { DATATEST } from "constants/integration";
import Image3DIcon from "../icons/image-3d";

// prettier-ignore
interface IProps {
    product: IItemRecord;
    onChange: (index: number) => void;
    selectedIndex: number;
    scope: IScope;
    selectOnHover?: boolean;
}

const MAX_TO_SHOW = 4;

export const Thumbs: FC<IProps> = (props) => {
    const { product, scope, onChange, selectedIndex, selectOnHover = true } = props;
    const useUnlimitedImages = useMemo(() => scope.brand?.hasPreference("unlimited_images"), [scope]);

    const media = useMemo(() => {
        const allMedia = product.getMedia(scope, "T");
        return useUnlimitedImages ? allMedia : _.filter(allMedia, { type: MEDIA_TYPE_IMAGE });
    }, [product, scope, useUnlimitedImages]);

    const handleSelect = useCallback((index: number) => () => onChange(index), [onChange]);

    const maxToShow = useMemo(() => Math.min(MAX_TO_SHOW, media.length), [media.length]);
    const sliderSettings = useMemo<Settings>(
        () => ({
            dots: false,
            lazyLoad: false,
            infinite: false,
            speed: 500,
            vertical: true,
            verticalSwiping: true,
            slidesToShow: maxToShow,
            slidesToScroll: maxToShow,
            nextArrow: <NextArrow />,
            prevArrow: <PrevArrow />,
        }),
        [maxToShow]
    );

    const slider = useRef(null);
    useEffect(() => {
        const shownItems: HTMLElement[] = slider.current?.innerSlider.list?.querySelectorAll(".slick-active");
        if (_.isEmpty(shownItems)) {
            return;
        }

        const lastShownIndex = Number(_.last(shownItems).getAttribute("data-index"));
        const firstShownIndex =
            lastShownIndex === media.length - 1
                ? lastShownIndex - maxToShow + 1
                : Number(_.first(shownItems).getAttribute("data-index"));

        if (!_.inRange(selectedIndex, firstShownIndex, lastShownIndex + 1)) {
            const slideStartIndex = Math.floor(selectedIndex / maxToShow) * maxToShow;
            slider.current?.slickGoTo(slideStartIndex);
        }
    }, [product, selectedIndex, maxToShow, media.length]);

    const getSafeImages = useGetSafeImages(scope);

    const getImageTemplate = useCallback(
        (image: IMediaImage) => (
            <ImageSafe
                alt={product.name}
                src={getSafeImages(image)}
                style={{ height: theme.PDPThumbnail.height, width: theme.PDPThumbnail.width }}
            />
        ),
        [product, getSafeImages]
    );

    const classes = useStyles({ hasArrows: media.length > MAX_TO_SHOW });
    return (
        <div className={classes.base} key={product?._id} data-test={DATATEST.PDP.THUMBS}>
            {!useUnlimitedImages && (
                <ul>
                    {_.map(media, ({ type, source }, index) => (
                        <li
                            key={index}
                            onClick={handleSelect(index)}
                            onMouseEnter={selectOnHover ? handleSelect(index) : null}
                        >
                            {type === MEDIA_TYPE_IMAGE && getImageTemplate({ type, source })}
                        </li>
                    ))}
                </ul>
            )}
            {useUnlimitedImages && (
                <Slider ref={slider} {...sliderSettings} className={classes.slider}>
                    {_.map(media, (item, index) => (
                        <div
                            data-test={DATATEST.PDP.THUMB_CONTAINER}
                            key={index}
                            className={classnames(index === selectedIndex && classes.selected, "media-item")}
                            onClick={handleSelect(index)}
                            onMouseEnter={selectOnHover ? handleSelect(index) : null}
                        >
                            {item.type === MEDIA_TYPE_IMAGE && getImageTemplate(item)}
                            {item.type === MEDIA_TYPE_VIDEO && <ThumbVideo poster={item.poster} />}
                            {item.type === MEDIA_TYPE_360 && <Thumb360 source={item.source} alt={product.name} />}
                            {item.type === MEDIA_TYPE_THREEDIUM && <ThumbThreedium />}
                        </div>
                    ))}
                </Slider>
            )}
        </div>
    );
};

const Thumb360: FC<{ source: string; alt: string }> = ({ source, alt }) => {
    const classes = use360Styles({});
    return (
        <div className={classes.wrapper} data-test={DATATEST.PDP.THUMBS_360}>
            <img src={source} alt={alt} />
            <div className={classes.iconButtonWrapper}>
                <IconButton className={classes.iconButton}>
                    <Image360Icon className={classes.icon} />
                </IconButton>
            </div>
        </div>
    );
};

const ThumbVideo: FC<{ poster?: string }> = ({ poster }) => {
    const classes = useVideoStyles({});
    return (
        <div className={classes.wrapper} data-test={DATATEST.PDP.THUMBS_VIDEO}>
            <VideoPoster poster={poster} />
            <div className={classes.iconWrapper}>
                <VideoPlayIcon className={classes.icon} />
            </div>
        </div>
    );
};

const ThumbThreedium: FC = () => {
    const classes = use360Styles({});
    return (
        <div className={classes.wrapper} data-test={DATATEST.PDP.THUMBS_THREEDIUM}>
            <div className={classes.iconButtonWrapper}>
                <IconButton className={classes.iconButton}>
                    <Image3DIcon className={classes.icon} />
                </IconButton>
            </div>
        </div>
    );
};

interface IArrowProps {
    className?: string;
    style?: object;
    onClick?: (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
}

const NextArrow = (props: IArrowProps) => {
    const { className, style, onClick } = props;
    const classes = useArrowStyles();
    return (
        <KeyboardArrowDownIcon
            className={classnames(className, classes.common, classes.next)}
            style={style}
            onClick={onClick}
            data-test={DATATEST.PDP.THUMBS_NEXT}
        />
    );
};

const PrevArrow = (props: IArrowProps) => {
    const { className, style, onClick } = props;
    const classes = useArrowStyles();
    return (
        <KeyboardArrowUpIcon
            className={classnames(className, classes.common, classes.prev)}
            style={style}
            onClick={onClick}
            data-test={DATATEST.PDP.THUMBS_PREV}
        />
    );
};

const useArrowStyles = makeStyles({
    common: {
        color: theme.palette.secondaryTextColor,
        display: "block",
        width: "100% !important",
        left: 0,
        fontSize: 14,
        "&.slick-disabled": {
            display: "none !important",
        },
    },
    prev: {
        top: -10,
    },
    next: {
        top: "unset",
        bottom: -20,
    },
});

const useStyles = makeStyles<Theme, { hasArrows: boolean }>({
    base: {
        flexGrow: 0,
        flexShrink: 0,
        margin: "0 12px 0 0",
        width: theme.PDPThumbnail.width,
        "& .media-item": {
            cursor: "pointer",
        },
        "& li": {
            cursor: "pointer",
            marginBottom: 12,
            "&:last-child": {
                marginBottom: 0,
            },
        },
        "& img": {
            height: theme.PDPThumbnail.height,
            width: theme.PDPThumbnail.width,
            objectFit: "contain",
            marginTop: "auto",
        },
    },
    slider: ({ hasArrows }) => ({
        marginTop: hasArrows ? 18 : 0,
        "& .slick-track": {
            height: "auto !important",
        },
    }),
    selected: {
        position: "relative",
        "&::before": {
            content: "''",
            height: 2,
            width: theme.PDPThumbnail.width,
            background: theme.palette.accent4Color,
            position: "absolute",
            bottom: 0,
            zIndex: 1,
        },
    },
});

const useVideoStyles = makeStyles({
    wrapper: {
        height: theme.PDPThumbnail.height,
        width: theme.PDPThumbnail.width,
        position: "relative",
        display: "flex",
    },
    iconWrapper: {
        display: "flex",
        position: "absolute",
        width: "100%",
        height: "100%",
        justifyContent: "center",
        alignItems: "center",
    },
    icon: {
        width: "26px !important",
        height: "26px !important",
        backgroundColor: "rgba(255,255,255,0.7)",
        borderRadius: "50%",
    },
});

const use360Styles = makeStyles({
    wrapper: {
        height: theme.PDPThumbnail.height,
        width: theme.PDPThumbnail.width,
        position: "relative",
    },
    icon: {
        width: "26px !important",
        height: "26px !important",
        backgroundColor: "rgba(245,245,245,0.7)",
        borderRadius: "50%",
        color: theme.palette.grey,
    },
    iconButtonWrapper: {
        width: "100%",
        height: "100%",
        position: "absolute",
        top: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1,
    },
    iconButton: {
        width: 35,
        height: 35,
        padding: 0,
    },
});

export default Thumbs;
